import React, { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { hasAnyPermission, PermissionUser } from '../lib/permissions';
import { Menu, X, LogOut, LayoutDashboard, ShoppingCart, ChefHat, Briefcase, Users, ClipboardList, Settings, Image, Moon, Sun, Contact, Heart, Share2, Gift, BookOpen, Home, Award, Newspaper, MessageCircle, MessageSquare, Package, Building2, Smartphone, Tag, Route, TrendingUp, AlertTriangle, Bell, TicketPercent, ChevronRight, BarChart3, Store, Star, Calendar } from 'lucide-react';
import { apiFetch } from '../lib/api';
import { useState, useEffect } from 'react';

interface AdminLayoutProps {
  children: ReactNode;
  currentPage?: string;
  onLogout?: () => void;
}

interface AdminUser {
  id: number;
  username: string;
  role: string;
  display_name: string;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ElementType;
  roles: string[];
  permissions: string[];
  path: string;
}

interface MenuGroup {
  id: string;
  label: string;
  items: MenuItem[];
}

const MENU_GROUPS: MenuGroup[] = [
  {
    id: 'business',
    label: '業務營運',
    items: [
      { id: 'dashboard', label: '儀表板', icon: LayoutDashboard, roles: ['super_admin', 'admin'], permissions: ['dashboard:read'], path: '/admin/dashboard' },
      { id: 'orders', label: '訂單管理', icon: ShoppingCart, roles: ['super_admin', 'admin', 'estate_manager'], permissions: ['orders:read'], path: '/admin/orders' },
      { id: 'consolidations', label: '備貨集運', icon: Package, roles: ['super_admin', 'admin'], permissions: ['merchants:manage'], path: '/admin/consolidations' },
      { id: 'customers', label: '客戶清單', icon: Contact, roles: ['super_admin', 'admin'], permissions: ['customers:read'], path: '/admin/customers' },
      { id: 'conversations', label: 'WhatsApp 對話', icon: MessageCircle, roles: ['super_admin', 'admin'], permissions: ['conversations:read'], path: '/admin/conversations' },
      { id: 'products', label: '產品管理', icon: ChefHat, roles: ['super_admin', 'admin', 'supplier'], permissions: ['products:read'], path: '/admin/products' },
      { id: 'estate-dashboard', label: '屋苑業績', icon: Building2, roles: ['super_admin', 'admin', 'estate_manager'], permissions: ['estates:dashboard:read'], path: '/admin/estate-dashboard' },
      { id: 'analytics', label: '數據分析', icon: BarChart3, roles: ['super_admin', 'admin'], permissions: ['analytics:read'], path: '/admin/analytics' },
      { id: 'impact', label: '影響力儀表板', icon: TrendingUp, roles: ['super_admin', 'admin'], permissions: ['impact:read'], path: '/admin/impact' },
      { id: 'reviews', label: '客戶評價', icon: Star, roles: ['super_admin', 'admin'], permissions: ['merchants:manage'], path: '/admin/reviews' },
      { id: 'notifications', label: '通知中心', icon: Bell, roles: ['super_admin', 'admin', 'supplier', 'estate_manager'], permissions: [], path: '/admin/notifications' },
      { id: 'package-configs', label: '套餐配置', icon: Package, roles: ['super_admin', 'admin'], permissions: ['packages:read'], path: '/admin/package-configs' },
    ],
  },
  {
    id: 'supplier-hub',
    label: '供應中心',
    items: [
      { id: 'supplier-prep-summary', label: '備貨摘要', icon: ClipboardList, roles: ['super_admin', 'admin', 'supplier'], permissions: ['supplier_orders:read'], path: '/admin/supplier/prep-summary' },
      { id: 'supplier-orders', label: '訂單明細', icon: ShoppingCart, roles: ['super_admin', 'admin', 'supplier'], permissions: ['supplier_orders:read'], path: '/admin/supplier/orders' },
      { id: 'supplier-sales-trends', label: '銷量趨勢', icon: TrendingUp, roles: ['super_admin', 'admin', 'supplier'], permissions: ['supplier_analytics:read'], path: '/admin/supplier/sales-trends' },
      { id: 'supplier-stock-warnings', label: '庫存警告', icon: AlertTriangle, roles: ['super_admin', 'admin', 'supplier'], permissions: ['products:read'], path: '/admin/supplier/stock-warnings' },
    ],
  },
  {
    id: 'web-design',
    label: '網頁設計',
    items: [
      { id: 'home-page', label: '首頁內容', icon: Home, roles: ['super_admin', 'admin'], permissions: ['cms:read'], path: '/admin/pages/home' },
      { id: 'brand-story', label: '品牌故事', icon: BookOpen, roles: ['super_admin', 'admin'], permissions: ['cms:read'], path: '/admin/pages/brand-story' },
      { id: 'testimonials', label: '權威見證', icon: Award, roles: ['super_admin', 'admin'], permissions: ['cms:read'], path: '/admin/testimonials' },
      { id: 'leaderboard', label: '好餸傳愛榜', icon: Award, roles: ['super_admin', 'admin'], permissions: ['cms:read'], path: '/admin/leaderboard' },
      { id: 'student-stories', label: '學員故事', icon: BookOpen, roles: ['super_admin', 'admin'], permissions: ['cms:read'], path: '/admin/stories' },
      { id: 'media-coverage', label: '媒體報導', icon: Newspaper, roles: ['super_admin', 'admin'], permissions: ['cms:read'], path: '/admin/media-coverage' },
    ],
  },
  {
    id: 'marketing',
    label: '行銷推廣',
    items: [
      { id: 'referrals', label: '推薦網絡', icon: Share2, roles: ['super_admin', 'admin'], permissions: ['referrals:read'], path: '/admin/referrals' },
      { id: 'merchants', label: '鄰里商家', icon: Store, roles: ['super_admin', 'admin'], permissions: ['merchants:manage'], path: '/admin/merchants' },
      { id: 'broadcast', label: '廣播推廣', icon: Briefcase, roles: ['super_admin'], permissions: ['broadcast:send'], path: '/admin/broadcast' },
      { id: 'auto-tags', label: '自動標籤', icon: Tag, roles: ['super_admin'], permissions: ['customer_journeys:manage'], path: '/admin/auto-tags' },
      { id: 'customer-journeys', label: '客戶旅程', icon: Route, roles: ['super_admin'], permissions: ['customer_journeys:manage'], path: '/admin/customer-journeys' },
      { id: 'gift-catalog', label: '禮品目錄', icon: Gift, roles: ['super_admin'], permissions: ['customer_journeys:manage'], path: '/admin/gift-catalog' },
      { id: 'pending-gifts', label: '待送禮品', icon: Package, roles: ['super_admin'], permissions: ['customer_journeys:manage'], path: '/admin/pending-gifts' },
      { id: 'vouchers', label: '回贈券', icon: TicketPercent, roles: ['super_admin'], permissions: ['customer_journeys:manage'], path: '/admin/vouchers' },
    ],
  },
  {
    id: 'system',
    label: '系統管理',
    items: [
      { id: 'whatsapp-center', label: 'WhatsApp 中心', icon: Smartphone, roles: ['super_admin', 'admin'], permissions: ['whatsapp:manage'], path: '/admin/whatsapp-center' },
      { id: 'cloudwapi-templates', label: 'CloudWAPI 訊息模板', icon: MessageSquare, roles: ['super_admin', 'admin'], permissions: ['whatsapp:manage'], path: '/admin/cloudwapi-templates' },
      { id: 'media-library', label: '媒體庫', icon: Image, roles: ['super_admin', 'admin', 'supplier'], permissions: ['media:read'], path: '/admin/media-library' },
      { id: 'users', label: '用戶管理', icon: Users, roles: ['super_admin'], permissions: ['users:read'], path: '/admin/users' },
      { id: 'estates', label: '屋苑管理', icon: Building2, roles: ['super_admin', 'admin'], permissions: ['estates:read'], path: '/admin/estates' },
      { id: 'audit-logs', label: '操作日誌', icon: ClipboardList, roles: ['super_admin'], permissions: ['audit:read'], path: '/admin/audit-logs' },
      { id: 'settings', label: '系統設置', icon: Settings, roles: ['super_admin'], permissions: ['settings:read'], path: '/admin/settings' },
    ],
  },
  {
    id: 'other',
    label: '其他',
    items: [
      { id: 'goodstore', label: 'SEN 計劃報名', icon: Calendar, roles: ['super_admin', 'admin'], permissions: ['customers:read'], path: '/admin/goodstore' },
      { id: 'ahjim-content', label: '💛 阿占隨意', icon: Heart, roles: ['super_admin'], permissions: ['ahjim:read'], path: '/admin/ahjim-content' },
    ],
  },
];

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(() => localStorage.getItem('admin_sidebar_open') !== 'false');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<AdminUser | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingTestimonialCount, setPendingTestimonialCount] = useState(0);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('admin_theme');
    return saved === 'dark';
  });
  // 手風琴收納：hover 大類時子分類向下滑出，其餘大類順勢往下移；滑出 nav 後回到當前頁所屬大類
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem('admin_user');
    if (stored) {
      try {
        setUser(JSON.parse(stored));
      } catch { /* ignore */ }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('admin_theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  useEffect(() => {
    localStorage.setItem('admin_sidebar_open', sidebarOpen ? 'true' : 'false');
  }, [sidebarOpen]);

  // 未讀通知數（每 60 秒輪詢）
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      apiFetch('/api/public/notifications/unread-count')
        .then(res => (res.ok ? res.json() : null))
        .then(data => { if (!cancelled && data) setUnreadCount(Number(data.count) || 0); })
        .catch(() => {});
    };
    load();
    const timer = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  // 待審核好餸傳愛榜數量（每 60 秒輪詢）— 僅有 cms:read 權限的管理員才查詢
  useEffect(() => {
    if (!user || !hasAnyPermission(user as PermissionUser | null, ['cms:read'])) {
      setPendingTestimonialCount(0);
      return;
    }
    let cancelled = false;
    const load = () => {
      apiFetch('/api/public/admin/leaderboard/pending-count')
        .then(res => (res.ok ? res.json() : null))
        .then(data => { if (!cancelled && data) setPendingTestimonialCount(Number(data.count) || 0); })
        .catch(() => {});
    };
    load();
    const timer = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [user]);

  // 供應商低庫存橫額
  useEffect(() => {
    if (user?.role !== 'supplier') { setLowStockCount(0); return; }
    let cancelled = false;
    apiFetch('/api/public/supplier/stock-warnings?below=1')
      .then(res => (res.ok ? res.json() : []))
      .then(data => { if (!cancelled && Array.isArray(data)) setLowStockCount(data.length); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.role]);

  const visibleGroups = MENU_GROUPS.map(group => ({
    ...group,
    items: group.items.filter(item => {
      if (!user) return true;
      // Prefer permission check; fall back to role check for backward compatibility
      if (item.permissions?.length) {
        return hasAnyPermission(user as PermissionUser | null, item.permissions);
      }
      return item.roles.includes(user.role);
    }),
  })).filter(group => group.items.length > 0);

  const flatMenuItems = visibleGroups.flatMap(group => group.items);

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      if (confirm('確定要登出嗎？')) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      }
    }
  };

  const isCurrentPage = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const activeGroupId = visibleGroups.find(g => g.items.some(item => isCurrentPage(item.path)))?.id || null;
  const expandedGroupId = openGroupId ?? activeGroupId;

  const getRoleLabel = (role?: string) => {
    switch (role) {
      case 'super_admin': return '系統管理員';
      case 'admin': return '管理員';
      case 'supplier': return '產品供應商';
      case 'estate_manager': return '屋苑管理員';
      default: return role || '';
    }
  };

  return (
    <div className={`flex h-screen ${isDark ? 'dark' : ''}`}>
      {/* Desktop Sidebar */}
      <div
        className={`hidden md:flex ${
          sidebarOpen ? 'w-64' : 'w-20'
        } bg-gradient-to-b from-orange-600 to-orange-800 text-white transition-all duration-300 flex-col shadow-lg dark:from-gray-900 dark:to-gray-950`}
      >
        {/* Logo/Header */}
        <div className="h-20 flex items-center justify-between px-4 border-b border-orange-500/50 dark:border-gray-700">
          <div className={`flex items-center gap-2 ${!sidebarOpen && 'justify-center w-full'}`}>
            <div className="bg-white rounded-lg p-2">
              <LayoutDashboard className="text-orange-600" size={20} />
            </div>
            {sidebarOpen && (
              <div className="flex flex-col">
                <span className="font-bold text-white text-sm">好餸</span>
                <span className="text-xs text-orange-200 dark:text-gray-400">管理後台</span>
              </div>
            )}
          </div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-orange-200 hover:text-white transition-colors dark:text-gray-400"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* User Info */}
        {sidebarOpen && user && (
          <div className="px-4 py-3 border-b border-orange-500/50 dark:border-gray-700">
            <div className="text-sm font-semibold">{user.display_name || user.username}</div>
            <div className="text-xs text-orange-200 dark:text-gray-400">{getRoleLabel(user.role)}</div>
          </div>
        )}

        {/* Navigation Menu — 展開時手風琴收納：hover 大類向下滑出子分類；收合時維持純圖標 */}
        <nav className="flex-1 px-3 py-6 space-y-2 overflow-y-auto" onMouseLeave={() => setOpenGroupId(null)}>
          {visibleGroups.map(group => {
            const isActiveGroup = activeGroupId === group.id;
            const isExpanded = expandedGroupId === group.id;
            const groupHasUnread = group.items.some(i => i.id === 'notifications') && unreadCount > 0;

            if (!sidebarOpen) {
              return (
                <div key={group.id} className="space-y-1">
                  {group.items.map(item => {
                    const Icon = item.icon;
                    const isActive = isCurrentPage(item.path);
                    return (
                      <button
                        key={item.id}
                        onClick={() => navigate(item.path)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                          isActive
                            ? 'bg-white text-orange-700 shadow-md font-semibold dark:bg-gray-700 dark:text-orange-400'
                            : 'text-orange-100 hover:bg-orange-500 hover:text-white dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
                        }`}
                        title={item.label}
                      >
                        <span className="relative flex-shrink-0">
                          <Icon size={20} />
                          {item.id === 'notifications' && unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border border-orange-700" />
                          )}
                          {item.id === 'leaderboard' && pendingTestimonialCount > 0 && (
                            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border border-orange-700" />
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            }

            return (
              <div key={group.id} onMouseEnter={() => setOpenGroupId(group.id)}>
                <button
                  onClick={() => setOpenGroupId(isExpanded ? null : group.id)}
                  className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg transition-all duration-200 ${
                    isExpanded
                      ? 'bg-orange-500 text-white dark:bg-gray-700'
                      : isActiveGroup
                        ? 'text-white font-semibold hover:bg-orange-500 dark:hover:bg-gray-700'
                        : 'text-orange-100 hover:bg-orange-500 hover:text-white dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
                  }`}
                >
                  <span className="flex items-center gap-2 text-sm">
                    {group.label}
                    {groupHasUnread && <span className="w-2 h-2 bg-red-500 rounded-full" />}
                  </span>
                  <ChevronRight size={14} className={`flex-shrink-0 transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`} />
                </button>

                {/* 子分類：grid-rows 0fr→1fr 高度動畫，向下順滑展開 */}
                <div className={`grid transition-all duration-300 ease-in-out ${isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                  <div className="overflow-hidden">
                    <div className="mt-1 ml-3 pl-3 space-y-1 border-l border-orange-500/40 dark:border-gray-700">
                      {group.items.map(item => {
                        const Icon = item.icon;
                        const isActive = isCurrentPage(item.path);
                        return (
                          <button
                            key={item.id}
                            onClick={() => navigate(item.path)}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 ${
                              isActive
                                ? 'bg-white text-orange-700 shadow-md font-semibold dark:bg-gray-700 dark:text-orange-400'
                                : 'text-orange-100 hover:bg-orange-500 hover:text-white dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
                            }`}
                          >
                            <Icon size={18} className="flex-shrink-0" />
                            <span className="flex-1 text-left text-sm">{item.label}</span>
                            {item.id === 'notifications' && unreadCount > 0 && (
                              <span className="min-w-[20px] h-5 px-1.5 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full">
                                {unreadCount > 99 ? '99+' : unreadCount}
                              </span>
                            )}
                            {item.id === 'leaderboard' && pendingTestimonialCount > 0 && (
                              <span className="min-w-[20px] h-5 px-1.5 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full">
                                {pendingTestimonialCount > 99 ? '99+' : pendingTestimonialCount}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        {/* Theme Toggle + Logout */}
        <div className="px-3 py-4 border-t border-orange-500/50 dark:border-gray-700 space-y-2">
          <button
            onClick={() => setIsDark(!isDark)}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-orange-100 hover:bg-orange-500 hover:text-white transition-all duration-200 dark:text-gray-300 dark:hover:bg-gray-700"
            title={!sidebarOpen ? (isDark ? '亮色模式' : '暗黑模式') : ''}
          >
            {isDark ? <Sun size={20} className="flex-shrink-0" /> : <Moon size={20} className="flex-shrink-0" />}
            {sidebarOpen && <span className="text-sm font-medium">{isDark ? '亮色模式' : '暗黑模式'}</span>}
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-orange-100 hover:bg-red-600 hover:text-white transition-all duration-200 dark:text-gray-300"
            title={!sidebarOpen ? '登出' : ''}
          >
            <LogOut size={20} className="flex-shrink-0" />
            {sidebarOpen && <span className="text-sm font-medium">登出</span>}
          </button>
        </div>
      </div>


      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 w-64 bg-gradient-to-b from-orange-600 to-orange-800 text-white flex flex-col shadow-lg z-50 md:hidden dark:from-gray-900 dark:to-gray-950">
            {/* Mobile Sidebar Header */}
            <div className="h-20 flex items-center justify-between px-4 border-b border-orange-500/50 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <div className="bg-white rounded-lg p-2">
                  <LayoutDashboard className="text-orange-600" size={20} />
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-white text-sm">好餸</span>
                  <span className="text-xs text-orange-200 dark:text-gray-400">管理後台</span>
                </div>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="text-orange-200 hover:text-white transition-colors dark:text-gray-400"
              >
                <X size={20} />
              </button>
            </div>

            {/* Mobile User Info */}
            {user && (
              <div className="px-4 py-3 border-b border-orange-500/50 dark:border-gray-700">
                <div className="text-sm font-semibold">{user.display_name || user.username}</div>
                <div className="text-xs text-orange-200 dark:text-gray-400">{getRoleLabel(user.role)}</div>
              </div>
            )}

            {/* Mobile Navigation Menu */}
            <nav className="flex-1 px-3 py-6 space-y-6 overflow-y-auto">
              {visibleGroups.map(group => (
                <div key={group.id}>
                  <div className="px-4 mb-2 text-xs font-semibold text-orange-200/80 dark:text-gray-400 uppercase tracking-wider">
                    {group.label}
                  </div>
                  <div className="space-y-1">
                    {group.items.map(item => {
                      const Icon = item.icon;
                      const isActive = isCurrentPage(item.path);
                      return (
                        <button
                          key={item.id}
                          onClick={() => {
                            navigate(item.path);
                            setMobileMenuOpen(false);
                          }}
                          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                            isActive
                              ? 'bg-white text-orange-700 shadow-md font-semibold dark:bg-gray-700 dark:text-orange-400'
                              : 'text-orange-100 hover:bg-orange-500 hover:text-white dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
                          }`}
                        >
                          <Icon size={20} className="flex-shrink-0" />
                          <span className="text-sm flex-1">{item.label}</span>
                          {item.id === 'notifications' && unreadCount > 0 && (
                            <span className="min-w-[20px] h-5 px-1.5 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full">
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                          )}
                          {item.id === 'leaderboard' && pendingTestimonialCount > 0 && (
                            <span className="min-w-[20px] h-5 px-1.5 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full">
                              {pendingTestimonialCount > 99 ? '99+' : pendingTestimonialCount}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>

            {/* Mobile Theme Toggle + Logout */}
            <div className="px-3 py-4 border-t border-orange-500/50 dark:border-gray-700 space-y-2">
              <button
                onClick={() => setIsDark(!isDark)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-orange-100 hover:bg-orange-500 hover:text-white transition-all duration-200 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {isDark ? <Sun size={20} className="flex-shrink-0" /> : <Moon size={20} className="flex-shrink-0" />}
                <span className="text-sm font-medium">{isDark ? '亮色模式' : '暗黑模式'}</span>
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-orange-100 hover:bg-red-600 hover:text-white transition-all duration-200 dark:text-gray-300"
              >
                <LogOut size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">登出</span>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden bg-gray-100 dark:bg-gray-900 dark:text-gray-300 transition-colors">
        {/* Top Bar */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm px-4 md:px-6 py-4 flex items-center justify-between transition-colors">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden p-2 -ml-2 text-gray-600 hover:text-orange-600 dark:text-gray-300 dark:hover:text-orange-400"
              aria-label="打開選單"
            >
              <Menu size={24} />
            </button>
            <h1 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-white truncate">
              {flatMenuItems.find(item => isCurrentPage(item.path))?.label || '好餸 - 管理後台'}
            </h1>
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 hidden sm:block">
            {new Date().toLocaleDateString('zh-HK', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </div>
        </div>

        {/* Supplier Low-Stock Banner */}
        {user?.role === 'supplier' && lowStockCount > 0 && (
          <button
            onClick={() => navigate('/admin/supplier/stock-warnings')}
            className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 flex items-center justify-center gap-2 text-sm font-medium transition-colors"
          >
            <AlertTriangle size={16} />
            ⚠️ {lowStockCount} 款產品低於庫存門檻，請盡快補貨 — 點擊查看
          </button>
        )}

        {/* Page Content */}
        <div className="flex-1 overflow-auto overflow-x-auto">
          <div className="p-4 md:p-6 min-w-fit">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
